import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  HiOutlineCloudUpload,
  HiVideoCamera,
  HiPhotograph,
} from "react-icons/hi";
import { toast } from "react-toastify";
import { generateFileUploadUrl } from "../../services/traditionalWorkouts";

interface TraditionalWorkoutFileUploadProps {
  acceptedTypes: string[]; // e.g., ['image/jpeg', 'image/png', 'video/mp4']
  fileType: "image" | "video";
  maxSize?: number;
  defaultPreview?: {
    name?: string;
    url?: string;
    type?: string;
  };
  onUploadComplete?: (fileId: string, storageKey: string) => void;
  onFileSelected?: (file: File) => void;
  onUploadStart?: () => void;
  onUploadError?: () => void;
}

interface UploadState {
  progress: number;
  status: "idle" | "uploading" | "uploaded" | "failed";
  fileName?: string;
  previewUrl?: string;
}

const TraditionalWorkoutFileUpload: React.FC<
  TraditionalWorkoutFileUploadProps
> = ({
  acceptedTypes = [],
  fileType,
  maxSize = 1024 * 1024 * 1024, // 1GB default
  defaultPreview,
  onUploadComplete,
  onFileSelected,
  onUploadStart,
  onUploadError,
}) => {
  const [uploadState, setUploadState] = useState<UploadState>({
    progress: 0,
    status: "idle",
    fileName: defaultPreview?.name,
    previewUrl: defaultPreview?.url,
  });

  // Update preview when defaultPreview prop changes (for edit mode)
  // Only update if we don't already have a preview or if status is idle
  useEffect(() => {
    if (
      defaultPreview &&
      uploadState.status === "idle" &&
      !uploadState.previewUrl
    ) {
      setUploadState((prev) => ({
        ...prev,
        fileName: defaultPreview.name,
        previewUrl: defaultPreview.url,
      }));
    }
  }, [defaultPreview, uploadState.status, uploadState.previewUrl]);

  const formattedMaxSize =
    maxSize >= 1024 * 1024 * 1024
      ? Math.round(maxSize / (1024 * 1024 * 1024)) + " GB"
      : Math.round(maxSize / (1024 * 1024)) + " MB";

  const validateFile = (file: File): void => {
    if (acceptedTypes.length && !acceptedTypes.includes(file.type)) {
      throw new Error("Invalid file type.");
    }

    if (file.size > maxSize) {
      throw new Error(`File is too large. Max size is ${formattedMaxSize}.`);
    }
  };

  const uploadFileToS3 = async (
    presignedUrl: string,
    file: File,
  ): Promise<void> => {
    // Create a fresh axios instance without auth interceptors
    const s3Axios = axios.create();

    await s3Axios.put(presignedUrl, file, {
      headers: {
        "Content-Type": file.type,
      },
      onUploadProgress: (progressEvent) => {
        const progress = progressEvent.total
          ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
          : 0;

        setUploadState((prev) => ({
          ...prev,
          progress,
        }));
      },
    });
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      if (!file) return; // Type guard to ensure file is defined

      try {
        // Validate file
        validateFile(file);

        // Call onFileSelected callback if provided
        if (onFileSelected) {
          onFileSelected(file);
        }

        // Call onUploadStart callback if provided
        if (onUploadStart) {
          onUploadStart();
        }

        // Create preview URL for images
        let previewUrl: string | undefined;
        if (file.type.startsWith("image/")) {
          previewUrl = URL.createObjectURL(file);
        }

        setUploadState({
          progress: 0,
          status: "uploading",
          fileName: file.name,
          previewUrl,
        });

        // Step 1: Get presigned URL
        const uploadUrlResponse = await generateFileUploadUrl({
          filename: file.name,
          content_type: file.type,
        });

        if (!uploadUrlResponse.results) {
          throw new Error("Failed to generate upload URL");
        }

        const { upload_url, file_id, storage_key } = uploadUrlResponse.results;

        // Step 2: Upload to S3
        await uploadFileToS3(upload_url, file);

        // Step 3: Update state and notify parent
        setUploadState((prev) => ({
          ...prev,
          progress: 100,
          status: "uploaded",
        }));

        if (onUploadComplete) {
          onUploadComplete(file_id, storage_key);
        }

        toast.success(
          `${fileType === "image" ? "Image" : "Video"} uploaded successfully!`,
        );
      } catch (error) {
        setUploadState((prev) => ({
          ...prev,
          status: "failed",
        }));

        // Call onUploadError callback if provided
        if (onUploadError) {
          onUploadError();
        }

        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error("An unknown error occurred.");
        }
      }
    },
    [
      acceptedTypes,
      maxSize,
      fileType,
      onUploadComplete,
      onFileSelected,
      onUploadStart,
      onUploadError,
    ],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes.length
      ? acceptedTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {})
      : undefined,
    multiple: false,
  });

  const truncateFileName = (name: string) => {
    const maxLength = 30;
    if (name.length > maxLength) {
      return name.substring(0, maxLength) + "...";
    }
    return name;
  };

  const getFileExtensions = (mimeTypes: string[]) => {
    return mimeTypes
      .map((mime) => {
        const parts = mime.split("/");
        return parts[1] ? parts[1].toUpperCase() : "";
      })
      .join(", ");
  };

  // Determine what to show as preview
  let previewContent: React.ReactNode;

  if (uploadState.status === "uploading") {
    previewContent = (
      <div className="flex flex-col items-center justify-center h-[100px] mb-2">
        {fileType === "video" ? (
          <HiVideoCamera size={60} className="text-gray-500 -mb-1" />
        ) : (
          <HiPhotograph size={60} className="text-gray-500 -mb-1" />
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {truncateFileName(uploadState.fileName || "")}
        </p>
        <div className="w-full max-w-[200px] bg-gray-200 rounded-full h-2 mt-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${uploadState.progress}%` }}
          ></div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {uploadState.progress}%
        </p>
      </div>
    );
  } else if (uploadState.previewUrl && fileType === "image") {
    previewContent = (
      <img
        src={uploadState.previewUrl}
        alt="Preview"
        className="mb-2 max-h-[100px] object-contain"
      />
    );
  } else if (fileType === "video" && uploadState.previewUrl) {
    // Show thumbnail image for video if available
    previewContent = (
      <div className="flex flex-col items-center justify-center h-[100px] mb-2">
        <img
          src={uploadState.previewUrl}
          alt="Video thumbnail"
          className="max-h-[80px] object-contain rounded"
        />
        {uploadState.fileName && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {truncateFileName(uploadState.fileName)}
          </p>
        )}
      </div>
    );
  } else if (uploadState.fileName && fileType === "video") {
    previewContent = (
      <div className="flex flex-col items-center justify-center h-[100px] mb-2">
        <HiVideoCamera size={60} className="text-gray-500 -mb-1" />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {truncateFileName(uploadState.fileName)}
        </p>
      </div>
    );
  } else {
    previewContent = (
      <HiOutlineCloudUpload
        size={60}
        className="text-gray-500 mb-2 h-[100px]"
      />
    );
  }

  const getStatusText = () => {
    if (uploadState.status === "uploading") return "Uploading...";
    if (uploadState.fileName || defaultPreview?.url) return "Click to replace";
    return "Click to upload";
  };

  return (
    <div
      {...getRootProps()}
      className={`flex mt-1 h-64 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-500 ${
        isDragActive ? "bg-blue-100" : "bg-gray-50"
      } hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 ${
        uploadState.status === "uploading" ? "pointer-events-none" : ""
      }`}
    >
      <input
        {...getInputProps()}
        disabled={uploadState.status === "uploading"}
      />
      <div className="flex flex-col items-center justify-center pb-6 pt-5">
        {previewContent}
        <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
          <span className="font-semibold">
            {isDragActive ? "Drop file" : getStatusText()}
          </span>{" "}
          {uploadState.status !== "uploading" && "or drag and drop"}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {acceptedTypes.length
            ? getFileExtensions(acceptedTypes.slice(0, 3))
            : ""}{" "}
          (Max: {formattedMaxSize})
        </p>
      </div>
    </div>
  );
};

export default TraditionalWorkoutFileUpload;
